/**
 * CascadeTimeline.tsx
 *
 * Read-only view of the OKR Tracker cascade history for one employee.
 * The cascade ITSELF is owned by `okr.rascalrepublic.com` — this is
 * purely a tracer/audit lens. EPR never writes to /cascadedTasks.
 *
 * Drop this anywhere a per-employee context is useful:
 *   - Step 1 Add-employee form  → HR sees what's already cascaded
 *   - EPR self-review form      → employee sees their own context
 *   - Appraisal review          → HR cross-references KR delivery
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchCascadeForEmployee, deriveTimeline,
  type CascadeTracerEntry, type CascadeStatus, type TimelineEvent,
} from '../../services/cascadeTracerService';
import DisclosureBadge from './DisclosureBadge';

export interface CascadeTimelineProps {
  email: string;
  /** Filter by cycle year. Omit to show every year. */
  year?: number;
  /** Compact mode for embedding in dense lists. */
  compact?: boolean;
}

const STATUS_TONE: Record<CascadeStatus, 'success' | 'info' | 'warning' | 'neutral'> = {
  'Done':         'success',
  'In Progress':  'info',
  'Pending':      'warning',
  'Blocked':      'warning',
};

const STATUS_ICON: Record<CascadeStatus, string> = {
  'Done':         'check_circle',
  'In Progress':  'sync',
  'Pending':      'schedule',
  'Blocked':      'block',
};

const CascadeTimeline: React.FC<CascadeTimelineProps> = ({ email, year, compact }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<CascadeTracerEntry[]>([]);
  const [counts, setCounts] = useState<Record<CascadeStatus, number> | null>(null);
  const [ownerFound, setOwnerFound] = useState(true);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchCascadeForEmployee({ email, year });
      setEntries(r.entries);
      setCounts(r.counts);
      setOwnerFound(r.ownerId !== null || r.entries.length > 0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [email, year]);

  useEffect(() => { void load(); }, [load]);

  if (!email) return null;

  if (loading) {
    return (
      <div className="text-xs text-stone-500 flex items-center gap-2">
        <span className="material-symbols-rounded animate-spin" style={{ fontSize: 14 }}>sync</span>
        Loading cascaded tasks…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
        Cascade tracer failed: {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-xs text-stone-500">
        {ownerFound
          ? `No cascaded tasks for this employee${year ? ` in ${year}` : ''}.`
          : 'Employee has no OKR Tracker account yet — no cascade history.'
        }
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Status summary */}
      {counts && !compact && (
        <div className="flex flex-wrap gap-2 text-xs">
          {(['Pending', 'In Progress', 'Done', 'Blocked'] as CascadeStatus[]).map(s => (
            counts[s] > 0 && (
              <DisclosureBadge
                key={s}
                icon={STATUS_ICON[s]}
                label={`${counts[s]} ${s}`}
                tone={STATUS_TONE[s]}
              />
            )
          ))}
        </div>
      )}

      {/* Entries */}
      <div className="space-y-2">
        {entries.map(e => (
          <CascadeEntryCard key={e.id} entry={e} compact={!!compact} />
        ))}
      </div>
    </div>
  );
};

// ─── Card ──────────────────────────────────────────────────────────

const CascadeEntryCard: React.FC<{ entry: CascadeTracerEntry; compact: boolean }> = ({
  entry, compact,
}) => {
  const events = deriveTimeline(entry);
  return (
    <section className="rounded-lg border border-stone-200 bg-white px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-stone-900 truncate">
            {entry.krNumber && (
              <span className="text-stone-500 mr-1">{entry.krNumber}</span>
            )}
            {entry.title}
          </div>
          <div className="text-xs text-stone-500 mt-0.5 truncate">
            {entry.objectiveTitle ?? '(unknown objective)'}
            {entry.quarter && <> · {entry.quarter}</>}
            {entry.dueDate && <> · due {formatDate(entry.dueDate)}</>}
          </div>
        </div>
        <DisclosureBadge
          icon={STATUS_ICON[entry.status]}
          label={`${entry.status} · ${entry.progress}%`}
          tone={STATUS_TONE[entry.status]}
        />
      </div>

      {!compact && events.length > 0 && (
        <ol className="mt-2 pl-3 border-l-2 border-stone-200 space-y-1">
          {events.map((ev: TimelineEvent, idx: number) => (
            <li key={idx} className="text-xs text-stone-600 flex items-baseline gap-2">
              <span className="text-stone-400 tabular-nums">{formatDate(ev.ts)}</span>
              <span>{ev.label}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

// ─── Helpers ───────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default CascadeTimeline;
